import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SearchResult } from '@ukam/core/searchs/search-result';
import { API_BASE_URL } from '@ukam/core/tokens/api-base-url.token';
import { StoredFile, StoredFileDownloadUrlResult, StoredFileUploadIntentResult } from '../models/stored-file';
import { StoredFileUploadParams } from '../params/stored-file-upload.params';

export interface StoredFileListQuery {
  /** Storage (disco). */
  storageId?: string | null;
  /** Ambos opcionais e independentes; ausentes = arquivos sem esse vínculo. */
  feature?: string | null;
  /** Id público da entidade dona; exige feature. */
  parentId?: string | null;
  /** @deprecated uso interno; prefira parentId. */
  parentNid?: number | null;
  includeHistory?: boolean;
  /** Pasta lógica (StoredFolder). Nulo = todas as pastas, salvo rootOnly. */
  folderId?: string | null;
  /** Só arquivos na raiz do dono (pasta nid 0). Ignorado quando folderId é informado. */
  rootOnly?: boolean;
  /** Paginação: sem limit a consulta vem inteira. */
  offset?: number;
  limit?: number;
}

/** Espelha StoredFileMoveParams (C#). folderId vazio = raiz. */
export interface StoredFileMoveParams {
  folderId?: string | null;
}

@Injectable({ providedIn: 'root' })
export class StoredFileApiService {
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly http = inject(HttpClient);
  private readonly url = `${this.apiBaseUrl}/files`;

  /** Consulta paginada no padrão do sistema (POST /files/search com StoredFileFilter). */
  search(filter: Record<string, unknown>): Observable<SearchResult<StoredFile>> {
    return this.http.post<SearchResult<StoredFile>>(`${this.url}/search`, filter);
  }

  /** Listagem de uma pasta/entidade. Monta o StoredFileFilter a partir da query. */
  list(query: StoredFileListQuery): Observable<StoredFile[]> {
    return this.listPage(query).pipe(map(result => result?.items ?? []));
  }

  /** Mesma consulta do list, devolvendo o resultado paginado (count/offset/limit/pages). */
  listPage(query: StoredFileListQuery): Observable<SearchResult<StoredFile>> {
    const paged = (query.limit ?? 0) > 0;
    const filter: Record<string, unknown> = paged
      ? { offset: query.offset ?? 0, limit: query.limit, currentOnly: !query.includeHistory }
      : { unpaged: true, limit: 0, currentOnly: !query.includeHistory };
    if (query.storageId) filter['storage'] = { id: { eq: query.storageId } };
    if (query.feature) filter['feature'] = { key: { eq: query.feature } };
    if (query.parentId) filter['parentId'] = query.parentId;
    else if ((query.parentNid ?? 0) > 0) filter['parentNid'] = { eq: query.parentNid };
    if (query.folderId) filter['folderId'] = { eq: query.folderId };
    else if (query.rootOnly) filter['rootOnly'] = true;
    return this.search(filter);
  }

  getById(id: string): Observable<StoredFile> {
    return this.http.get<StoredFile>(`${this.url}/${id}`);
  }

  getByKey(key: string): Observable<StoredFile> {
    return this.http.get<StoredFile>(`${this.url}/key/${encodeURIComponent(key)}`);
  }

  createUploadIntent(params: StoredFileUploadParams): Observable<StoredFileUploadIntentResult> {
    return this.http.post<StoredFileUploadIntentResult>(`${this.url}/upload-intents`, params);
  }

  completeUpload(id: string): Observable<StoredFile> {
    return this.http.post<StoredFile>(`${this.url}/${id}/complete`, {});
  }

  /** Move o arquivo para outra pasta do mesmo dono. Não toca no objeto físico. */
  move(id: string, params: StoredFileMoveParams): Observable<StoredFile> {
    return this.http.post<StoredFile>(`${this.url}/${id}/move`, params);
  }

  /** Renomeia o arquivo (originalName). Não toca no objeto físico, cuja chave é imutável. */
  rename(id: string, name: string): Observable<StoredFile> {
    return this.http.post<StoredFile>(`${this.url}/${id}/rename`, { name });
  }

  getDownloadUrl(id: string): Observable<StoredFileDownloadUrlResult> {
    return this.http.get<StoredFileDownloadUrlResult>(`${this.url}/${id}/download-url`);
  }

  delete(id: string, note?: string | null): Observable<StoredFile> {
    let params = new HttpParams();
    if (note) params = params.set('note', note);
    return this.http.delete<StoredFile>(`${this.url}/${id}`, { params });
  }
}
